import React from 'react';
import InfoTooltip from './InfoTooltip';
import BarthelEvaluation from './BarthelEvaluation';

export default function Step2Parameters({ selectedDiseases, formData, setFormData, onNext, onPrev }) {

  const handleScaleChange = (boalaId, scalaId, valoare) => {
    setFormData(prev => ({
      ...prev,
      [boalaId]: {
        ...prev[boalaId],
        scale: {
          ...(prev[boalaId]?.scale || {}),
          [scalaId]: valoare
        }
      }
    }));
  };

  const handleDocChange = (boalaId, docName, isChecked) => {
    setFormData(prev => ({
      ...prev,
      [boalaId]: {
        ...prev[boalaId],
        documente: {
          ...(prev[boalaId]?.documente || {}),
          [docName]: isChecked
        }
      }
    }));
  };

  const handleCheckboxChange = (boalaId, field, isChecked) => {
    setFormData(prev => ({
      ...prev,
      [boalaId]: {
        ...prev[boalaId],
        [field]: isChecked
      }
    }));
  };

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Pasul 2: Evaluare Funcțională și Documente</h2>
      <p style={{ color: '#7f8c8d', marginTop: 0, marginBottom: '24px', fontSize: '14px' }}>
        Completați datele clinice și confirmați existența documentelor obligatorii.
        <InfoTooltip 
          title="Ce trebuie completat la acest pas?" 
          text={`La acest pas trebuie să introduceți informațiile medicale pentru fiecare afecțiune selectată.\n\n🔬 SCALE DE EVALUARE:\nSelectați gradul de severitate conform documentelor medicale pe care le aveți (referate, analize, investigații). Fiecare opțiune arată ce grad de handicap ar rezulta.\n\n📋 EVALUAREA AUTONOMIEI (ADL/IADL):\nBifați doar dacă persoana are dependență totală / necesită asistent personal conform Scalei Barthel sau evaluării ADL/IADL.\n\n📄 DOCUMENTE:\nBifați documentele pe care le aveți deja în dosar. Cele nebifate vor apărea ca „lipsă" la Pasul 3, scăzând încrederea rezultatului.`} 
        />
      </p>

      {selectedDiseases.map(boala => (
        <div key={boala.id_boala} className="card" style={{ marginBottom: '24px', padding: '24px', borderLeft: '4px solid #0056b3' }}>
          <h3 style={{ color: '#0056b3', marginTop: 0, marginBottom: '20px', fontSize: '17px', paddingBottom: '12px', borderBottom: '1px solid #e8f0fe' }}>
            {boala.nume}
            <span style={{ display: 'block', fontSize: '12px', color: '#888', fontWeight: 400, marginTop: '4px' }}>{boala.categorie}</span>
          </h3>

          {/* Scale Medicale */}
          {boala.criterii_exactitate.scale_obligatorii && boala.criterii_exactitate.scale_obligatorii.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#2c3e50', margin: '0 0 12px 0', fontSize: '15px' }}>
                🔬 Scale de Evaluare
                <InfoTooltip 
                  title="Scale de Evaluare Funcțională" 
                  text={`Selectați nivelul de severitate al afecțiunii conform documentelor medicale.\n\nFiecare opțiune corespunde unui grad de handicap:\n• Mediu – afectare moderată a funcționalității\n• Accentuat – afectare semnificativă, limitări importante\n• Grav – dependență parțială sau totală, posibil asistent personal\n\n⚠️ Dacă nu aveți documentele necesare sau nu știți exact unde se încadrează, consultați medicul specialist care a emis referatul.`} 
                />
              </h4>
              {boala.criterii_exactitate.scale_obligatorii.map(scala => (
                <div key={scala.id} style={{ marginBottom: '16px', paddingLeft: '8px' }}>
                  <strong style={{ fontSize: '14px', color: '#555' }}>{scala.nume_scala}:</strong>
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {Object.keys(scala.valori_permise).map(valoare => {
                      const grad = scala.valori_permise[valoare];
                      const isSelected = formData[boala.id_boala]?.scale?.[scala.id] === valoare;
                      const gradColor = grad === 'Grav' ? '#dc3545' : grad === 'Accentuat' ? '#e67e22' : grad === 'Mediu' ? '#0056b3' : '#666';
                      
                      return (
                        <label 
                          key={valoare} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            cursor: 'pointer',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: isSelected ? `2px solid ${gradColor}` : '2px solid transparent',
                            background: isSelected ? `${gradColor}11` : '#f8f9fa',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <input 
                            type="radio" 
                            name={`${boala.id_boala}_${scala.id}`}
                            value={valoare}
                            checked={isSelected}
                            onChange={(e) => handleScaleChange(boala.id_boala, scala.id, e.target.value)}
                            style={{ marginRight: '10px', marginTop: '2px', accentColor: gradColor }}
                          />
                          <div>
                            <span style={{ fontSize: '13px', color: '#444' }}>{valoare}</span>
                            <span style={{ 
                              display: 'inline-block',
                              marginLeft: '8px',
                              padding: '2px 8px', 
                              borderRadius: '4px', 
                              fontSize: '11px', 
                              fontWeight: 700,
                              color: 'white',
                              background: gradColor
                            }}>
                              {grad}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Evaluarea Autonomiei – Barthel / ADL / IADL */}
          {boala.criterii_exactitate.dependenta_ajutor && (
            <BarthelEvaluation 
              formData={formData} 
              setFormData={setFormData} 
              boalaId={boala.id_boala} 
              evalGroup={boala.eval_group}
            />
          )}

          {/* Istoric Medical / Vârstă Debut */}
          {boala.criterii_exactitate.istoric_medical?.necesita_debut && (
            <div style={{ marginBottom: '20px', background: '#f5f5f5', padding: '16px 20px', borderRadius: '10px', border: '1px solid #e0e0e0' }}>
              <h4 style={{ color: '#37474f', marginTop: 0, marginBottom: '8px', fontSize: '14px' }}>
                📅 Condiții privind Istoricul Medical
                <InfoTooltip 
                  title="Istoric Medical – Vârsta de debut" 
                  text={`Unele afecțiuni (ex: TSA, retard mintal) necesită dovada că debutul a fost înainte de o anumită vârstă.\n\nDocumentele acceptate:\n• Bilete de externare vechi\n• Scrisori medicale din copilărie\n• Certificate de la medic de familie\n• Fișa de dispensarizare\n\nDacă nu aveți aceste documente, rezultatul final va fi marcat ca „incert".`} 
                />
              </h4>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px 0' }}>{boala.criterii_exactitate.istoric_medical.avertisment_debut}</p>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#37474f', fontSize: '14px' }}>
                <input 
                  type="checkbox" 
                  checked={!!formData[boala.id_boala]?.debut}
                  onChange={(e) => handleCheckboxChange(boala.id_boala, 'debut', e.target.checked)}
                  style={{ marginRight: '10px' }}
                />
                <span>DA, există acte care confirmă debutul la vârsta cerută.</span>
              </label>
            </div>
          )}

          {/* Checklist Documente */}
          {boala.criterii_exactitate.documente_necesare && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ color: '#2c3e50', margin: '0 0 12px 0', fontSize: '14px' }}>
                📄 Documente Necesare la Dosar
                <InfoTooltip 
                  title="Documente obligatorii pentru dosarul de handicap" 
                  text={`Bifați documentele pe care le aveți deja pregătite pentru depunerea la Serviciul de Evaluare Complexă (SEC) / Comisia de Evaluare.\n\nDocumentele nebifate:\n• Vor fi afișate la Pasul 3 ca „lipsă"\n• Scad scorul de încredere al evaluării\n• Trebuie obținute înainte de depunerea dosarului\n\n💡 Sfat: Referatul medical trebuie să fie recent (max. 6 luni de la emitere). Verificați valabilitatea înainte de depunere!`} 
                />
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '4px' }}>
                {boala.criterii_exactitate.documente_necesare.map(doc => (
                  <label key={doc} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    background: formData[boala.id_boala]?.documente?.[doc] ? '#e8f5e9' : '#fafafa',
                    border: formData[boala.id_boala]?.documente?.[doc] ? '1px solid #a5d6a7' : '1px solid #eee',
                    transition: 'all 0.15s ease'
                  }}>
                    <input 
                      type="checkbox" 
                      checked={!!formData[boala.id_boala]?.documente?.[doc]}
                      onChange={(e) => handleDocChange(boala.id_boala, doc, e.target.checked)}
                      style={{ marginRight: '10px', accentColor: '#28a745' }}
                    />
                    <span style={{ fontSize: '13px', color: formData[boala.id_boala]?.documente?.[doc] ? '#2e7d32' : '#555' }}>{doc}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Investigații Necesare */}
          {boala.criterii_exactitate.investigatii_necesare && boala.criterii_exactitate.investigatii_necesare.length > 0 && (
            <div style={{ marginTop: '16px', background: 'linear-gradient(135deg, #e8eaf6, #e3f2fd)', padding: '16px 20px', borderRadius: '10px', border: '1px solid #90caf9' }}>
              <h4 style={{ color: '#1565c0', marginTop: 0, marginBottom: '10px', fontSize: '14px' }}>
                🔬 Investigații și Examinări Necesare
                <InfoTooltip 
                  title="Investigații medicale necesare pentru evaluare" 
                  text={`Această listă conține toate investigațiile, analizele și examinările medicale pe care comisia le poate solicita pentru a evalua corect gradul de handicap.\n\n📌 IMPORTANT:\n• Nu toate investigațiile sunt obligatorii – lista este orientativă.\n• Medicul specialist va indica ce investigații sunt relevante pentru cazul dvs.\n• Prezentarea investigațiilor complete crește acuratețea evaluării.\n\n💡 Sfat: Solicitați-le din timp de la medicul curant, deoarece unele pot necesita programare cu câteva săptămâni în avans.`} 
                />
              </h4>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#1a237e' }}>
                {boala.criterii_exactitate.investigatii_necesare.map((inv, idx) => (
                  <li key={idx} style={{ marginBottom: '4px' }}>{inv}</li>
                ))}
              </ul>
            </div>
          )}
          
        </div>
      ))}

      <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'space-between' }} className="no-print">
        <button onClick={onPrev} className="btn btn-secondary">
          ← Înapoi
        </button>
        <button onClick={onNext} className="btn btn-primary">
          Calculează Soluția →
        </button>
      </div>
    </div>
  );
}
